/**
 * @fileoverview Password field molecule.
 * @description Composes Label, Input, and IconButton into a password field with a show/hide
 * toggle, so the visibility state stays local to the field instead of leaking into form state.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'

import eyeIcon from '@/assets/svg/login/icon-eye.svg'
import IconButton from '@/shared/components/atoms/IconButton'
import Input from '@/shared/components/atoms/Input'
import Label from '@/shared/components/atoms/Label'

type PasswordFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  id: string
  label: string
  error?: string | undefined
  'data-testid'?: string
}

/**
 * @component
 * @param {PasswordFieldProps} props - Standard input attributes (minus type) plus id/label/error.
 * @param {string} props.id - Also used to associate the label with the input.
 * @param {string} props.label - The visible field label.
 * @param {string} [props.error] - Validation message shown below the field, if any.
 * @param {string} [props['data-testid']] - QA locator for the input. The show/hide toggle and the
 * error message derive theirs from it by swapping a trailing "-input" for "-toggle-button" /
 * "-error" (e.g. login-password-input -> login-password-toggle-button).
 */
function PasswordField({
  id,
  label,
  error,
  'data-testid': testId,
  ...inputProps
}: PasswordFieldProps) {
  const testIdBase = testId?.replace(/-input$/, '')
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative mt-2">
        <Input
          id={id}
          type={isVisible ? 'text' : 'password'}
          hasTrailingAddon
          invalid={Boolean(error)}
          aria-invalid={Boolean(error)}
          data-testid={testId}
          {...inputProps}
        />
        <IconButton
          aria-label={isVisible ? 'Hide password' : 'Show password'}
          data-testid={testIdBase ? `${testIdBase}-toggle-button` : undefined}
          onClick={() => setIsVisible((current) => !current)}
        >
          <img src={eyeIcon} alt="" className="h-5 w-5" />
        </IconButton>
      </div>
      {error && (
        <p
          className="mt-1.5 text-[13px] text-red-600"
          data-testid={testIdBase ? `${testIdBase}-error` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default PasswordField
